/**
 * Booking rules: a row that comes back - the account fee every month, the SVS every quarter - is
 * booked the way a person booked it once, when they asked for that ("book future rows like
 * this"). A rule belongs to one account and matches the counterparty and the direction of the
 * money; it never touches a row a person decided on (`matchedBy: "person"`), so a booking someone
 * undid stays undone, and it never books a row that might be a duplicate.
 */
import type { BookingInput, Transaction } from "@/lib/api/banking";

export type BookingRule = BookingInput & {
  id: string;
  account: string;
  /** `counterpartyKey` of the rows it books. */
  counterparty: string;
  direction: "out" | "in";
};

/**
 * The counterparty as rules compare it: lower case, letters only, so "SVS 1/2026 Vers.Nr 4711"
 * and "SVS 2/2026 Vers.Nr 4711" are the same. The description stands in when a bank leaves the
 * counterparty empty (George's own fees).
 */
export function counterpartyKey(row: Pick<Transaction, "counterparty" | "description">): string {
  const text = (row.counterparty || row.description || "").toLowerCase();
  return text
    .replace(/[^\p{L}]+/gu, " ")
    .trim()
    .slice(0, 200);
}

export const direction = (row: Pick<Transaction, "amount">): "out" | "in" =>
  row.amount < 0 ? "out" : "in";

/** Rows nobody has explained or decided on yet. */
function open(row: Transaction): boolean {
  return (
    row.status === "unmatched" &&
    !row.document &&
    row.matchedBy !== "person" &&
    !row.possibleDuplicateOf &&
    row.amount !== 0
  );
}

/** Every open row a rule books, with its rule. */
export function ruleBookings(
  transactions: Transaction[],
  rules: BookingRule[],
): Array<{ transaction: Transaction; rule: BookingRule }> {
  const byKey = new Map(
    rules.map((rule) => [`${rule.account}|${rule.counterparty}|${rule.direction}`, rule]),
  );
  return transactions.flatMap((transaction) => {
    if (!open(transaction)) return [];
    const key = counterpartyKey(transaction);
    if (!key) return [];
    const rule = byKey.get(`${transaction.account}|${key}|${direction(transaction)}`);
    return rule ? [{ transaction, rule }] : [];
  });
}
